// The Visualize list is GET /layers?view=meta. One layer's payload is
// GET /layers/{id}, and only when that layer is shown or prefetched.
// Color scales stay the stored meta range. A timeseries detail is the step
// manifest plus the default step (the neighbour prefetch lives on the
// covariate scrubber, not here).
import type { QueryClient } from "@tanstack/react-query";
import { api } from "../api";
import type {
  LayerMeta,
  LayerMetaDocument,
  ManifestLayer,
} from "../types/contract";

export const LAYER_LIST_STALE_MS = 5 * 60_000;
export const LAYER_DETAIL_GC_MS = 30 * 60_000;
export const HOVER_PREFETCH_MS = 150;
export const HOVER_PREFETCH_MAX = 2;

export interface LayerListData {
  document: LayerMetaDocument;
  etag: string | null;
}

export interface LayerDetailRequest {
  id: string;
  contentVersion: number | string | null | undefined;
}

export function versionKey(value: number | string | null | undefined): string {
  return value == null || value === "" ? "" : String(value);
}

export function layerListKey(fieldId: string) {
  return ["layers", fieldId, "list"] as const;
}

export function layerDetailKey(
  fieldId: string,
  layerId: string,
  contentVersion: number | string | null | undefined,
) {
  return [
    "layers",
    fieldId,
    "detail",
    layerId,
    versionKey(contentVersion),
  ] as const;
}

function isBoundaryMeta(layer: LayerMeta): boolean {
  if (layer.id === "boundary") return true;
  return (
    layer.kind !== "cog" &&
    (layer.order ?? -1) === 0 &&
    /boundary/i.test(layer.title ?? "")
  );
}

export function listStamp(data: LayerListData | undefined): string {
  if (!data) return "";
  return JSON.stringify({
    etag: data.etag,
    boundary: data.document.boundary != null,
    layers: data.document.layers.map((layer) => [
      layer.id,
      layer.title,
      layer.kind,
      layer.content_version ?? null,
      layer.value_min ?? null,
      layer.value_max ?? null,
      layer.colormap ?? null,
      layer.order ?? null,
      layer.default_visible ?? null,
      layer.has_timeseries,
      layer.default_step ?? null,
    ]),
  });
}

export function layersFromDocument(
  document: LayerMetaDocument,
): ManifestLayer[] {
  const boundaryAt = document.layers.findIndex(isBoundaryMeta);
  return document.layers.map((meta, index) => {
    const layer = metaToLayer(meta);
    if (index === boundaryAt && document.boundary)
      layer.inline = document.boundary;
    return layer;
  });
}

export function metaToLayer(meta: LayerMeta): ManifestLayer {
  return {
    id: meta.id,
    kind: (meta.kind || "geojson") as ManifestLayer["kind"],
    title: meta.title,
    category: meta.category ?? undefined,
    colormap: meta.colormap ?? undefined,
    categories: meta.categories ?? undefined,
    value_min: meta.value_min ?? undefined,
    value_max: meta.value_max ?? undefined,
    unit: meta.unit ?? undefined,
    default_visible: meta.default_visible ?? false,
    default_opacity: meta.default_opacity ?? 1,
    order: meta.order ?? 0,
    content_version: versionKey(meta.content_version),
    is_stale: false,
  };
}

// The legend reads value_min/value_max off the layer. Those stay the meta
// values even after the payload arrives, so a loaded raster cannot rescale it.
export function mergeDetail(
  meta: ManifestLayer,
  detail: ManifestLayer | undefined,
): ManifestLayer {
  if (!detail) return meta;
  return {
    ...meta,
    ...detail,
    id: meta.id,
    title: detail.title || meta.title,
    value_min: meta.value_min ?? detail.value_min,
    value_max: meta.value_max ?? detail.value_max,
    colormap: meta.colormap ?? detail.colormap,
    categories: meta.categories ?? detail.categories,
    unit: meta.unit ?? detail.unit,
    inline: detail.inline ?? meta.inline,
    signed_url: detail.signed_url ?? meta.signed_url,
  };
}

export function documentFromLayers(
  layers: readonly ManifestLayer[],
): LayerMetaDocument {
  let boundary: LayerMetaDocument["boundary"] = null;
  const metas: LayerMeta[] = layers.map((layer) => {
    if (!boundary && looksLikeBoundary(layer)) {
      const inline = layer.inline;
      if (inline && typeof inline === "object")
        boundary = inline as LayerMetaDocument["boundary"];
    }
    return {
      id: layer.id,
      title: layer.title,
      kind: layer.kind,
      category: layer.category ?? null,
      order: layer.order,
      default_opacity: layer.default_opacity,
      default_visible: layer.default_visible,
      colormap: layer.colormap ?? null,
      categories: layer.categories ?? null,
      value_min: layer.value_min ?? null,
      value_max: layer.value_max ?? null,
      unit: layer.unit ?? null,
      dates: [],
      has_timeseries: false,
      default_step: null,
      content_version: layer.content_version,
    };
  });
  return { layers: metas, boundary };
}

function looksLikeBoundary(layer: ManifestLayer): boolean {
  if (layer.id === "boundary") return true;
  const type = (layer.inline as { type?: string } | null | undefined)?.type;
  return (
    layer.kind !== "cog" &&
    layer.order === 0 &&
    (type === "Polygon" ||
      type === "MultiPolygon" ||
      type === "Feature" ||
      type === "FeatureCollection" ||
      /boundary/i.test(layer.title ?? ""))
  );
}

export function layerHasPayload(layer: ManifestLayer): boolean {
  return layer.inline != null || Boolean(layer.signed_url);
}

export async function loadFieldLayerList(
  fieldId: string,
  opts: {
    ifNoneMatch?: string | null;
    signal?: AbortSignal;
    previous?: LayerListData;
  } = {},
): Promise<LayerListData> {
  const res = await api.fetchFieldLayerList(fieldId, {
    ifNoneMatch: opts.ifNoneMatch ?? undefined,
    signal: opts.signal,
  });
  if (res.notModified) {
    if (opts.previous)
      return {
        document: opts.previous.document,
        etag: res.etag ?? opts.previous.etag,
      };
    const again = await api.fetchFieldLayerList(fieldId, {
      signal: opts.signal,
    });
    if (!again.document) throw new Error("Layer list was empty.");
    return { document: again.document, etag: again.etag };
  }
  if (!res.document) throw new Error("Layer list was empty.");
  return { document: res.document, etag: res.etag };
}

export function fetchLayerDetailCached(
  qc: QueryClient,
  fieldId: string,
  layerId: string,
  contentVersion?: number | string | null,
): Promise<ManifestLayer> {
  return qc.fetchQuery({
    queryKey: layerDetailKey(fieldId, layerId, contentVersion),
    staleTime: Infinity,
    gcTime: LAYER_DETAIL_GC_MS,
    queryFn: ({ signal }) => api.fetchLayerDetail(fieldId, layerId, { signal }),
  });
}

const expiredOnce = new Set<string>();

// A COG signed URL that comes back 403 is minted again, once, for that layer.
export async function refetchExpiredLayer(
  qc: QueryClient,
  fieldId: string,
  layerId: string,
): Promise<ManifestLayer | null> {
  const list = qc.getQueryData<LayerListData>(layerListKey(fieldId));
  const meta = list?.document.layers.find((layer) => layer.id === layerId);
  const version = meta?.content_version;
  const once = `${fieldId}\n${layerId}\n${versionKey(version)}`;
  if (expiredOnce.has(once)) return null;
  expiredOnce.add(once);
  try {
    return await qc.fetchQuery({
      queryKey: layerDetailKey(fieldId, layerId, version),
      staleTime: 0,
      gcTime: LAYER_DETAIL_GC_MS,
      queryFn: ({ signal }) =>
        api.fetchLayerDetail(fieldId, layerId, { signal }),
    });
  } catch {
    return null;
  }
}

export function resetExpiredLayerRefetch(): void {
  expiredOnce.clear();
}

export function pointerCanHover(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function")
    return true;
  try {
    return !window.matchMedia("(hover: none)").matches;
  } catch {
    return true;
  }
}

export interface PrefetchJob {
  fieldId: string;
  layerId: string;
  contentVersion: number | string | null | undefined;
}

interface PrefetchBook {
  delayMs: number;
  maxInFlight: number;
  canHover: () => boolean;
  timers: Map<string, ReturnType<typeof setTimeout>>;
  inflight: Set<string>;
  queued: string[];
  jobs: Map<string, PrefetchJob>;
}

function prefetchKey(fieldId: string, layerId: string): string {
  return `${fieldId}\n${layerId}`;
}

function pumpPrefetch(qc: QueryClient, book: PrefetchBook): void {
  while (book.inflight.size < book.maxInFlight && book.queued.length) {
    const key = book.queued.shift();
    if (key && book.jobs.has(key)) void startPrefetch(qc, book, key);
  }
}

async function startPrefetch(
  qc: QueryClient,
  book: PrefetchBook,
  key: string,
): Promise<void> {
  const job = book.jobs.get(key);
  if (!job || book.inflight.has(key)) return;
  if (book.inflight.size >= book.maxInFlight) {
    if (!book.queued.includes(key)) book.queued.push(key);
    return;
  }
  book.inflight.add(key);
  try {
    await qc.prefetchQuery({
      queryKey: layerDetailKey(job.fieldId, job.layerId, job.contentVersion),
      staleTime: Infinity,
      gcTime: LAYER_DETAIL_GC_MS,
      queryFn: ({ signal }) =>
        api.fetchLayerDetail(job.fieldId, job.layerId, { signal }),
    });
  } catch {
    // A mouse-leave aborts the read. The click fetches it if it is still wanted.
  } finally {
    book.inflight.delete(key);
    pumpPrefetch(qc, book);
  }
}

export function createLayerPrefetcher(opts?: {
  delayMs?: number;
  maxInFlight?: number;
  canHover?: () => boolean;
}) {
  const book: PrefetchBook = {
    delayMs: opts?.delayMs ?? HOVER_PREFETCH_MS,
    maxInFlight: opts?.maxInFlight ?? HOVER_PREFETCH_MAX,
    canHover: opts?.canHover ?? pointerCanHover,
    timers: new Map(),
    inflight: new Set(),
    queued: [],
    jobs: new Map(),
  };
  return {
    enter(qc: QueryClient, job: PrefetchJob) {
      if (!book.canHover()) return;
      const key = prefetchKey(job.fieldId, job.layerId);
      book.jobs.set(key, job);
      const pending = book.timers.get(key);
      if (pending) clearTimeout(pending);
      book.timers.set(
        key,
        setTimeout(() => {
          book.timers.delete(key);
          void startPrefetch(qc, book, key);
        }, book.delayMs),
      );
    },
    leave(qc: QueryClient, fieldId: string, layerId: string) {
      const key = prefetchKey(fieldId, layerId);
      const pending = book.timers.get(key);
      if (pending) clearTimeout(pending);
      book.timers.delete(key);
      const at = book.queued.indexOf(key);
      if (at >= 0) book.queued.splice(at, 1);
      const job = book.jobs.get(key);
      if (!book.inflight.has(key) || !job) return;
      void qc.cancelQueries({
        queryKey: layerDetailKey(job.fieldId, job.layerId, job.contentVersion),
      });
      book.inflight.delete(key);
      pumpPrefetch(qc, book);
    },
    inflightCount() {
      return book.inflight.size;
    },
  };
}

export const layerPrefetch = createLayerPrefetcher();

export function cachedLayerRows(
  qc: QueryClient,
  fieldId: string,
): { id: string; title?: string | null }[] | undefined {
  const list = qc.getQueryData<LayerListData>(layerListKey(fieldId));
  if (list) return list.document.layers;
  return qc.getQueryData<{ id: string; title?: string | null }[]>([
    "layers",
    fieldId,
  ]);
}
